'use client';

import React from 'react';
import cloneDeep from 'lodash/cloneDeep';
import { EmployerContext } from '@/context/create-context';
import { UserProps } from '@/context/auth';
import { JobType } from '@/components/employer/jobs-page';
import {
  updateCompany,
  updateEmployerProfile,
  getEmployerProfile,
} from '@/api/tanzhi';
import { JobOpportunityType } from '@/components/job-opportunity-panel';
import { HotCitiesType } from '@/components/city/CityCard';

interface Option {
  id: string;
  name: string;
}

interface Props {
  children: React.ReactNode;
  companyData: CompanyValueType;
}

export type EmployerProps = UserProps & Props;

export type BusinessLicenseType = {
  name: string;
  registered_address: string;
  unified_social_credit_code: string;
  legal_representative: string;
  registered_capital: string;
  date_of_establishment: string;
  business_type: string;
  operating_status: string;
  business_scope: null;
  license_picture: string;
};

export type BenefitType = {
  id: string;
  name: string;
  type: string;
};

export type WorkScheduleType = {
  rest_type: string;
  work_over: string;
  start_time: string;
  end_time: string;
  is_check_in: boolean;
  is_flexible_work: boolean;
};

export const defaultWorkScheduleValue = {
  rest_type: '',
  work_over: '',
  start_time: '',
  end_time: '',
  is_check_in: true,
  is_flexible_work: false,
};

export type CompanyBasicInfoType = {
  id: string;
  industry: { id: string; name: string } | null;
  location: { location: Option; address: string };
  name: string;
  size: string;
  financing_stage: string | null;
};

export const defaultCompanyBasicInfo = {
  id: '',
  industry: { id: '', name: '' },
  location: { location: { id: '', name: '' }, address: '' },
  name: '',
  size: '',
  financing_stage: '',
};

export type TeamType = {
  name: string;
  type: 'default' | 'custom';
  display: boolean;
  culture_preference: Record<string, number>;
};

export const CULTURE_PREFERENCE = 'culture_preference';
export const CULTURE_PREFERENCE_CHOICES = 'choices';
export const CULTURE_PREFERENCE_TYPE = 'type';
export const CULTURE_PREFERENCE_SCORE = 'score';

export type CulturePreferenceValueType = {
  [CULTURE_PREFERENCE_CHOICES]: number[][];
  [CULTURE_PREFERENCE_TYPE]: string;
  [CULTURE_PREFERENCE_SCORE]: number;
};

export type CompanyValueType = {
  type: null | string;
  logo: null | string;
  description: null | string;
  status: number;
  business_license: BusinessLicenseType | null;
  medias: { images: string[]; videos: string[] };
  team_executives: {
    photo: string;
    name: string;
    description: string;
    age_level: string;
    position: string;
  }[];
  products: {
    photo: string;
    name: string;
    description: string;
    link: string;
  }[];
  benefits: BenefitType[];
  work_schedule: WorkScheduleType;
  settings: {
    teams: TeamType[];
  };
  job_opportunity?: JobOpportunityType[];
  related_jobs?: JobOpportunityType[];
  cities?: HotCitiesType[];
  [CULTURE_PREFERENCE]: CulturePreferenceValueType;
} & CompanyBasicInfoType;

export type InterviewType = {
  id: string;
  title: string;
  subtitle: string;
  video: string;
  interviewer_profile: {
    name: string;
    avatar: string;
    feature: string[];
    summary: string;
  };
  company: {
    name: string;
    industry: Option | null;
    size: string | null;
    type: null | string;
    logo: null | string;
    description: null | string;
    benefits: BenefitType[];
    financing_stage: string | null;
    [CULTURE_PREFERENCE]: CulturePreferenceValueType;
  };
  jobs: JobType[];
  images: { workplace: string; interviewer: string };
  questions_and_answers: { question: string; answer: string }[];
};

export const defaultCulturePreferenceValue = {
  [CULTURE_PREFERENCE_CHOICES]: [],
  [CULTURE_PREFERENCE_TYPE]: '',
  [CULTURE_PREFERENCE_SCORE]: 0,
};

export const defaultCompanyValue = {
  business_license: null,
  description: null,
  logo: null,
  status: 0,
  type: null,
  medias: { images: [], videos: [] },
  team_executives: [],
  products: [],
  benefits: [],
  work_schedule: defaultWorkScheduleValue,
  settings: {
    teams: [],
  },
  [CULTURE_PREFERENCE]: defaultCulturePreferenceValue,
  ...defaultCompanyBasicInfo,
};

export type ProfileValueType = {
  name: string;
  email: string;
  position: string;
  company: { id: string; name: string } | null;
  trust_instrument: string;
};

export const defaultProfileValue = {
  name: '',
  email: '',
  position: '',
  company: null,
  trust_instrument: '',
};

export interface EmployerContextValue {
  profile: ProfileValueType;
  setProfile: React.Dispatch<React.SetStateAction<ProfileValueType>>;
  saveProfile: (profile: Partial<ProfileValueType>) => Promise<any>;
  company: CompanyValueType;
  setCompany: React.Dispatch<React.SetStateAction<CompanyValueType>>;
  saveCompany: (profile: Partial<CompanyValueType>) => Promise<any> | undefined;
}

export const defaultEmployerContext = {
  profile: defaultProfileValue,
  setProfile: () => {},
  saveProfile: () => new Promise(() => {}),
  company: defaultCompanyValue,
  setCompany: () => {},
  saveCompany: () => new Promise(() => {}),
};

export const EmployerProvider = (props: Props) => {
  const { children, companyData } = props;
  const [profile, setProfile] =
    React.useState<ProfileValueType>(defaultProfileValue);
  const [company, setCompany] = React.useState<CompanyValueType>(
    cloneDeep(companyData ? companyData : defaultCompanyValue),
  );

  React.useEffect(() => {
    getEmployerProfile().then((res) => {
      if (res.status === 200) {
        setProfile(res.data);
      }
    });
  }, []);

  const saveProfile = (profile: Partial<ProfileValueType>) =>
    updateEmployerProfile(profile);

  const saveCompany = (data: Partial<CompanyValueType>) => {
    if (company?.id) {
      return updateCompany(company.id, data);
    }
  };

  return (
    <EmployerContext.Provider
      value={{
        profile,
        setProfile,
        saveProfile,
        company,
        setCompany,
        saveCompany,
      }}
    >
      {children}
    </EmployerContext.Provider>
  );
};
